import React, { useState, useRef, useEffect } from 'react'
import { Input, Button } from 'semantic-ui-react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import { useKeyDown } from '../../../hooks/useKeyDown'
import {KeyboardKeys} from '../constants'

// Options:
// value: current value
// onChange: change callback
// min, max: minimum , maximum allowed values
// digits: number of digits after floating point
// buttonPlacement: button position [null, or "right" or "none"]
const NumericInput = ({ value, onChange,onChangeComplete, min, max, digits, buttonPlacement,disabled, ...rest }) => {
    const format = (val) => {
        if (val === "") {
            return ""
        }
        const flt = parseFloat(val)
        if (_.isFinite(flt)) {
            return flt.toFixed(_.isUndefined(digits) ? 2 : digits)
        } else {
            return ""
        }
    }

    const [displayValue, setDisplayValue] = useState(format(value))
    const ref = useRef()

    useEffect(() => {
        if (ref.current?.inputRef.current !== document.activeElement) {
            if (!isNaN(parseFloat(value))){
              setDisplayValue(format(value))
            }
        }
    }, [value])

    useEffect(() => {
        if (onChange && displayValue != value) {
          onChange(parseFloat(format(displayValue)));
        }
    }, [displayValue])
    
    const checkIfSameElements = () => ref.current?.inputRef.current === document.activeElement

    const UpKeyHandler = ({ key }) => {
      if (checkIfSameElements() && key == KeyboardKeys.ArrowUp) {
        increase();
      }
    };

    const DownKeyHandler = ({ key }) => {
      if (checkIfSameElements() && key == KeyboardKeys.ArrowDown) {
        decrease();
      }
    };

    const EnterKeyHandler = (e) => {
      if (e.key == KeyboardKeys.Enter){
        e.preventDefault() 
        //Fix for bug #5403 where pressing enter anywhere increases input due to improper focus.
      }
    };

    useKeyDown([UpKeyHandler, DownKeyHandler, EnterKeyHandler]);

    const handleChange = (e,{ value }) => {
            if (value === "" || value.match(/^[+-]?\d{0,3}(,|.?\d{3})*(\.|(\.\d+))?$/)) {
                let flt = parseFloat(value)
                if (_.isFinite(flt)) {
                    if (_.isNumber(min) && flt < min) {
                        return
                    }
                    if (_.isNumber(max) && flt > max) {
                        return
                    }
                }
                setDisplayValue(value)
                if (onChange) {
                    onChange(parseFloat(format(flt)))
                }
            }
    }

    const onBlur = () => {
        setDisplayValue(prev => format(prev))
        onChangeComplete?.(parseFloat(format(displayValue)))
    }

    const increase = () => {
        let val;
        setDisplayValue((prevValue)=> {
            prevValue = parseFloat(prevValue);
            if (prevValue + 1 >= max) val = max;
            else val = format(++prevValue);
            return val;
        })
        onChange?.(val)
        
    }

    const decrease = () => {
        let val;
        setDisplayValue((prevValue) => {
          prevValue = parseFloat(prevValue);
          if (prevValue - 1 <= min) val = min;
          else val = format(--prevValue);
          return val;
        });
        onChange?.(val)
        
    }

    const decreaseStyle = buttonPlacement === "right" ? { flex: 0.5, borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderTopRightRadius: 0, padding: "0em 0.2em" } : {}
    const increaseStyle = buttonPlacement === "right" ? { flex: 0.5, borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderBottomRightRadius: 0, padding: "0em 0.2em" } : { borderTopRightRadius: "0.285714rem", borderBottomRightRadius: "0.285714rem" }
    const decreaseButton = <Button icon={buttonPlacement === "right" ? "caret down" : "minus"} size="small" disabled={value <= min || disabled} style={decreaseStyle} onClick={decrease} />
    const increaseButton = <Button icon={buttonPlacement === "right" ? "caret up" : "plus"} size="small" disabled={value >= max ||disabled} style={increaseStyle} onClick={increase} />
    switch (buttonPlacement) {
        case "none":
            return <Input ref={ref} value={displayValue} onChange={handleChange} onBlur={onBlur} min={min} max={max} disabled={disabled} {...rest}></Input>
        case "right":
            return <Input actionPosition={buttonPlacement === "right" ? null : "left"} labelPosition="right" ref={ref} value={displayValue} onChange={handleChange} onBlur={onBlur} disabled={disabled} min={min} max={max} {...rest}>
                <input />
                <div style={{ display: "flex", flexDirection: "column" }}>
                    {increaseButton}
                    {decreaseButton}
                </div>
            </Input>
        default:
            return (
              <Input
                actionPosition={buttonPlacement === 'right' ? null : 'left'}
                labelPosition='right'
                ref={ref}
                value={displayValue}
                onChange={handleChange}
                onBlur={onBlur}
                disabled={disabled}
                min={min}
                max={max}
                {...rest}
              >
                {decreaseButton}
                <input />
                {increaseButton}
              </Input>
            );
    }
}

NumericInput.propTypes = {
    value: PropTypes.any,
    onChange: PropTypes.func,
    onChangeComplete: PropTypes.func,
    min: PropTypes.number,
    max: PropTypes.number,
    digits: PropTypes.number,
    buttonPlacement: PropTypes.oneOf(["right", "leftAndRight", "none"]),
    disabled: PropTypes.bool
}
export default NumericInput